import React from "react";
import { getRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CheckIconBlack } from "../../icons";
import PenIcon from "components/icons/PenIcon";

const { DateTime } = require("luxon");

class CJAffiliateMerchantData extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            merchants: [],
            merchantsLoading: false,
            MerchantsTable: {
                title: "Affiliate merchants",
                options: {
                    selectableRows: "multiple",
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'merchantName',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        this.getMerchants();
    };

    getMerchants = async () => {
        let url = `/api/Merchant/GetCJAffiliateMerchants`;

        this.setState({ merchantsLoading: true });

        try {
            const merchants = await getRequest(url);

            this.setState({
                merchants,
                merchantsLoading: false,
            });
        } catch (error) {
            console.error("Error fetching merchants:", error);
            this.setState({ merchants: [], merchantsLoading: false });
        }
    };

    onTabChange = (tab) => {
        this.setState({ tab });
    }

    render() {
        const columns = [
            {
                name: "hostname",
                label: "Hostname",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <span>{value}</span>;
                    }
                }
            },
            {
                name: "commission",
                label: "Commission",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value ? `${value * 100}%` : "-"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "advertiserId",
                label: "Advertiser ID",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <span>{value}</span>;
                    }
                }
            },
            {
                name: "accountStatus",
                label: "Account Status",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "Active" ? "🟢 Active" : "🔴 Inactive"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "sevenDayEpc",
                label: "7-Day EPC",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return `$${value.toFixed(2)}`;
                    }
                }
            },
            {
                name: "threeMonthEpc",
                label: "3-Month EPC",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return `$${value.toFixed(2)}`;
                    }
                }
            },
            {
                name: "language",
                label: "Language",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <span>{value}</span>;
                    }
                }
            },
            {
                name: "advertiserName",
                label: "Advertiser Name",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <b>{value}</b>;
                    }
                }
            },
            {
                name: "programUrl",
                label: "Program URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <a href={value} target="_blank" rel="noopener noreferrer">
                                {value}
                            </a>
                        );
                    }
                }
            },
            {
                name: "relationshipStatus",
                label: "Relationship Status",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "joined" ? "✅ Joined" : "❌ Not Joined"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "mobileTrackingCertified",
                label: "Mobile Tracking Certified",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "true" ? "✅ Yes" : "❌ No"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "cookieLessTrackingEnabled",
                label: "Cookie-less Tracking",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "true" ? "✅ Yes" : "❌ No"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "networkRank",
                label: "Network Rank",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return `${value}`;
                    }
                }
            },
            {
                name: "performanceIncentives",
                label: "Performance Incentives",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "true" ? "✅ Yes" : "❌ No"}
                            </p>
                        );
                    }
                }
            },
           {
            name: "primaryCategory",
            label: "Primary Category",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => {
                    if (!value || typeof value !== 'object') {
                        return "-";  // Handle missing or unexpected data gracefully
                    }

                    const { parent, child } = value;

                    if (!parent && !child) {
                        return "-"; // If both parent and child are undefined
                    }

                    return (
                        <div>
                            <p style={{ margin: 0 }}>{parent ?? "-"}</p>
                            <p style={{ margin: 0 }}>{child ?? "-"}</p>
                        </div>
                    );
                }
            }
        },
           {
            name: "actions",
            label: "Actions",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => {
                    if (!value || !Array.isArray(value) || value?.length === 0) {
                        return "-";  // Show "-" if no actions are available
                    }

                    return (
                        <ul style={{ paddingLeft: "20px", margin: 0 }}>
                            {value.map((action, index) => (
                                <li key={index}>
                                    {action.name} ({action.type}) - {action.commission ?? "N/A"}
                                </li>
                            ))}
                        </ul>
                    );
                }
            }
        },
            {
                name: "linkTypes",
                label: "Link Types",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value?.length > 0 ? value.join(", ") : "-";
                    }
                }
            }
        ];


        return (
            <div>
                <ThemeProvider theme={this.state.MerchantsTable.getTheme()}>
                    <MUIDataTable
                        title={
                            <div style={{ fontFamily: "'Archivo', sans-serif" }}>
                                <p style={{ fontSize: '22px', marginTop: "20px" }}>{this.props.affiliateProgramManager?.name + " merchants" ?? "No affiliate selected"}</p>
                            </div>
                        }
                        data={this.state.merchants}
                        columns={columns}
                        options={{
                            ...this.state.MerchantsTable.options,
                            textLabels: {
                                body: { noMatch: this.state.merchantsLoading ? "LOADING..." : "No data available" },
                            },
                            selectableRows: "none",
                        }}
                        ref={this.tableRef}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

export default CJAffiliateMerchantData;
